import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock, Mail } from 'lucide-react';
import { loginSchema, type LoginInput } from '@gbv/types';
import { AuthButton, AuthError, AuthField, AuthLayout, AuthLink } from '@gbv/ui';
import { useAuth } from '../stores/auth';
import { ApiRequestError } from '../lib/api';

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: LoginInput): Promise<void> {
    setFormError(null);
    try {
      await login(values.email, values.password);
    } catch (err) {
      setFormError(
        err instanceof ApiRequestError
          ? err.message
          : 'We could not reach the server. Check your connection and try again.',
      );
    }
  }

  return (
    <AuthLayout
      badge="Employee Access"
      headline={
        <>
          Run your day,
          <br />
          from one workspace.
        </>
      }
      description="Calls, WhatsApp, social media, appointments and CRM — everything you need to serve your assigned clients, in one login."
      features={[
        'Click-to-call & call logging',
        'WhatsApp inbox, all clients',
        'Social post scheduling',
        'Lead & CRM management',
      ]}
      cardTitle="Welcome back"
      cardDescription="Sign in to your workspace — calling, inbox, CRM and more."
      footnote="Secured access · visibility limited to your assigned clients"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <AuthField
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoFocus
          placeholder="you@company.com"
          icon={<Mail size={14} />}
          error={errors.email?.message}
          {...register('email')}
        />

        <div className="flex flex-col gap-1.5">
          <AuthField
            label="Password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••••"
            icon={<Lock size={14} />}
            error={errors.password?.message}
            {...register('password')}
          />
          <div className="flex justify-end">
            <AuthLink>Forgot password?</AuthLink>
          </div>
        </div>

        {formError && <AuthError>{formError}</AuthError>}

        <AuthButton isLoading={isSubmitting}>Sign in to workspace</AuthButton>
      </form>

      {import.meta.env.DEV && (
        <div className="mt-4 rounded-lg border border-auth-line bg-auth-field px-3 py-2.5">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-auth-dim">
            Seeded accounts — development only
          </p>
          <dl className="mt-1.5 space-y-0.5 font-mono text-[11px] text-auth-body">
            <div className="flex justify-between gap-3">
              <dt>karan@demo.test</dt>
              <dd className="text-auth-dim">Team lead · 3 clients</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>rahul@demo.test</dt>
              <dd className="text-auth-dim">Telecaller · 2 clients</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>priya@demo.test</dt>
              <dd className="text-auth-dim">WhatsApp · 1 client</dd>
            </div>
          </dl>
          <p className="mt-1.5 font-mono text-[11px] text-auth-dim">Password123!</p>
        </div>
      )}
    </AuthLayout>
  );
}
